import { computed, inject, NgZone } from "@angular/core";
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState
} from "@ngrx/signals";
import {
  IPictureInPictureHost,
  PICTURE_IN_PICTURE_WINDOW_SIZE,
  PictureInPictureTrigger
} from "../../core-ui.enums";
import { PublicParam } from "../../models/application-options";
import { AppConfigService } from "../app.config.service";
import { DeviceService } from "../device.service";
import {
  getDocumentPictureInPicture,
  PipDocumentSession
} from "../pip-document.utils";
import { debug, debugError } from "../utils";

export type PictureInPictureStatus = "closed" | "opening" | "open";

interface IPictureInPictureState {
  status: PictureInPictureStatus;
  trigger: PictureInPictureTrigger | null;
}

// not part of the MediaSessionAction type yet
const ENTER_PIP_ACTION = "enterpictureinpicture" as MediaSessionAction;

/**
 * Document Picture-in-Picture requires a top-level page (not an iframe) and is only
 * available on Chromium desktop browsers.
 */
export function isDocumentPictureInPictureSupported(): boolean {
  return (
    !!getDocumentPictureInPicture() &&
    !DeviceService.isIFrame &&
    !DeviceService.isMobile
  );
}

/**
 * State of the picture-in-picture call window. Provide it in the component that owns the
 * call UI (the room) and connect that component as the host.
 */
export const PictureInPictureStore = signalStore(
  withState<IPictureInPictureState>({ status: "closed", trigger: null }),
  withProps(() => ({
    _config: inject(AppConfigService),
    _zone: inject(NgZone)
  })),
  withProps(({ _config }) => ({
    isSupported:
      isDocumentPictureInPictureSupported() &&
      _config.publicParam(PublicParam.PICTURE_IN_PICTURE_ENABLED) !== false
  })),
  withComputed(({ status }) => ({
    isOpen: computed(() => status() === "open")
  })),
  withMethods((store) => {
    let host: IPictureInPictureHost | null = null;
    let pipWindow: Window | null = null;
    let session: PipDocumentSession | null = null;

    const restore = () => {
      if (store.status() === "closed") {
        return;
      }
      session?.destroy();
      session = null;
      pipWindow = null;
      patchState(store, { status: "closed", trigger: null });
      host?.pipClosed();
    };

    const onPageHide = () => store._zone.run(() => restore());

    const open = async (trigger: PictureInPictureTrigger) => {
      if (!store.isSupported || !host || store.status() !== "closed") {
        return;
      }
      patchState(store, { status: "opening", trigger });
      try {
        const win = await getDocumentPictureInPicture().requestWindow(
          PICTURE_IN_PICTURE_WINDOW_SIZE
        );
        pipWindow = win;
        session = new PipDocumentSession(document, win.document);
        session.init("pip-root");
        host.getPipElements().forEach((el) => session.adopt(el));
        // the pip window is another realm, its events are not patched by zone.js
        win.addEventListener("pagehide", onPageHide, { once: true });
        patchState(store, { status: "open" });
        debug("pip:opened", trigger);
        host.pipOpened(win);
      } catch (ex) {
        debugError("pip:open failed", ex);
        const win = pipWindow;
        restore();
        win?.close();
      }
    };

    const close = () => {
      const win = pipWindow;
      restore();
      win?.close();
    };

    /**
     * close and bring the call tab to the front. Browsers only switch tabs shortly after a
     * user gesture (e.g. a click in the pip window), otherwise focus() is ignored.
     */
    const returnToTab = () => {
      const wasOpen = store.status() !== "closed";
      close();
      if (wasOpen) {
        window.focus();
      }
    };

    return {
      connect(value: IPictureInPictureHost) {
        host = value;
      },
      disconnect() {
        close();
        host = null;
      },
      open,
      close,
      returnToTab,
      /** manual toggle from the stream controls. Closing returns the user to the call tab. */
      toggle() {
        if (store.status() === "open") {
          returnToTab();
        } else {
          open("manual");
        }
      },
      /** move an element that was re-created while the pip window is open */
      adopt(element: HTMLElement) {
        session?.adopt(element);
      },
      /** the pip window, while open */
      pipWindow(): Window | null {
        return pipWindow;
      }
    };
  }),
  withHooks((store) => {
    const onVisibilityChange = () => {
      // auto-opened windows close when the user returns to the tab, like Google Meet
      if (
        document.visibilityState === "visible" &&
        store.trigger() === "auto"
      ) {
        store._zone.run(() => store.close());
      }
    };

    return {
      onInit() {
        if (!store.isSupported) {
          return;
        }
        document.addEventListener("visibilitychange", onVisibilityChange);
        try {
          // Chrome calls this when the user switches tab while camera/mic are in use.
          // requestWindow() does not need a user gesture inside this handler.
          navigator.mediaSession?.setActionHandler(ENTER_PIP_ACTION, () =>
            store._zone.run(() => store.open("auto"))
          );
        } catch (ex) {
          debug("pip:auto enter not supported", ex);
        }
      },
      onDestroy() {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        try {
          navigator.mediaSession?.setActionHandler(ENTER_PIP_ACTION, null);
        } catch {
          // not supported
        }
        store.disconnect();
      }
    };
  })
);
